// ========================================
// APP.JS - Main Application Entry
// With React Query, Error Boundaries, Lazy Loading
// Updated with SuperAdmin and role-based routes
// ========================================

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Providers
import { queryClient } from './services/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { PiiRevealProvider } from './contexts/PiiRevealContext';

// Components
import ErrorBoundary from './components/ErrorBoundary';
import { PageLoader } from './components/Loading';

// ========================================
// EAGER LOADED PAGES (Critical Path)
// ========================================
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';

// ========================================
// LAZY LOADED PAGES
// ========================================
const Assets = lazy(() => import('./pages/Assets'));
const CreativeStudio = lazy(() => import('./pages/CreativeStudio'));
const AssetDetail = lazy(() => import('./pages/AssetDetail'));
const AssetCreate = lazy(() => import('./pages/AssetCreate'));
const AssetEdit = lazy(() => import('./pages/AssetEdit'));

const Clients = lazy(() => import('./pages/Clients'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const ClientCreate = lazy(() => import('./pages/ClientCreate'));
const ClientEdit = lazy(() => import('./pages/ClientEdit'));
const ClientImport = lazy(() => import('./pages/ClientImport'));

const PLRList = lazy(() => import('./pages/PLRList'));
const PLRCreate = lazy(() => import('./pages/PLRCreate'));
const PLREdit = lazy(() => import('./pages/PLREdit'));
const TemplateBuilder = lazy(() => import('./pages/TemplateBuilder'));

const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const PLRAnalyticsEnhanced = lazy(() => import('./pages/PLRAnalyticsEnhanced'));
const Reports = lazy(() => import('./pages/Reports'));


const Regions = lazy(() => import('./pages/Regions'));
const Users = lazy(() => import('./pages/Users'));
const Integrations = lazy(() => import('./pages/Integrations'));
// P3d connector directory — PREVIEW route only (/settings/connectors); does NOT
// replace /settings/integrations while Meta App Review is in progress.
const ConnectorDirectory = lazy(() => import('./pages/ConnectorDirectory'));
const PiiAccessLog = lazy(() => import('./pages/PiiAccessLog'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const CRMIntegrations = lazy(() => import('./pages/CRMIntegrations'));

const SpacesManagement = lazy(() => import('./pages/SpacesManagement'));
const SpacesImport = lazy(() => import('./pages/SpacesImport'));

const EmailCampaigns = lazy(() => import('./pages/EmailCampaigns'));
const EmailCampaignCreate = lazy(() => import('./pages/EmailCampaignCreate'));
const EmailCampaignDetail = lazy(() => import('./pages/EmailCampaignDetail'));
const WhatsAppCampaignCreate = lazy(() => import('./pages/WhatsAppCampaignCreate'));
const WhatsAppCampaignDetail = lazy(() => import('./pages/WhatsAppCampaignDetail'));

// Notifications
const Notifications = lazy(() => import('./pages/Notifications'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'));

// Residential Unit Management
const UnitTypesManagement = lazy(() => import('./pages/UnitTypesManagement'));
const UnitsImport = lazy(() => import('./pages/UnitsImport'));
const UnitsManagement = lazy(() => import('./pages/UnitsManagement'));
const UnitsImportByBuilding = lazy(() => import('./pages/UnitsImportByBuilding'));

// Agent
const AgentKB = lazy(() => import('./pages/AgentKB'));
const AgentConfig = lazy(() => import('./pages/AgentConfig'));
const SiteAgentProjects = lazy(() => import('./pages/SiteAgentProjects'));
const AgentConversations = lazy(() => import('./pages/AgentConversations'));
const Automations = lazy(() => import("./pages/Automations"));

// ========================================
// PROTECTED ROUTE WRAPPER
// ========================================
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader message="Checking authentication..." />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check role if specified
  if (allowedRoles && allowedRoles.length > 0) {
    // Normalize legacy roles for checking
    const normalizedRole = normalizeRole(user.role);
    if (!allowedRoles.includes(user.role) && !allowedRoles.includes(normalizedRole)) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

// Helper to normalize legacy roles
function normalizeRole(role) {
  const mapping = {
    'svp': 'client_admin',
    'sales': 'manager',
    'consultant': 'viewer',
    'client': 'viewer'
  };
  return mapping[role] || role;
}

// ========================================
// ADMIN ROUTE WRAPPER
// Only for client_admin, super_admin, svp
// ========================================
function AdminRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp']}>
      {children}
    </ProtectedRoute>
  );
}

// ========================================
// SUPER ADMIN ROUTE WRAPPER
// Only for super_admin
// ========================================
function SuperAdminRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['super_admin']}>
      {children}
    </ProtectedRoute>
  );
}

// ========================================
// PUBLIC SALES ROOMS are not rendered by this app. nginx sends /plr/*, every
// <sub>.archiverse.ai host and custom domains to the Next.js app (web/); the
// pre-cutover renderer that lived here (pages/PublicPLR.js, PublicTemplate.js
// and a subdomain shell) was unreachable and was removed on 2026-10-09.
// ========================================

// ========================================
// IMPERSONATION BANNER
// ========================================
function ImpersonationBanner() {
  const impersonationToken = localStorage.getItem('impersonation_token');
  const originalToken = localStorage.getItem('original_token');
  
  if (!impersonationToken || !originalToken) return null;
  
  const exitImpersonation = () => {
    localStorage.setItem('token', originalToken);
    localStorage.removeItem('impersonation_token');
    localStorage.removeItem('original_token');
    window.location.href = '/admin';
  };
  
  return (
    <div className="bg-yellow-500 text-yellow-900 px-4 py-2 text-center text-sm font-medium">
      ⚠️ You are impersonating another user.{' '}
      <button onClick={exitImpersonation} className="underline hover:no-underline">
        Exit Impersonation
      </button>
    </div>
  );
}

// ========================================
// MAIN APP COMPONENT
// ========================================
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TenantProvider>
           <PiiRevealProvider>
            <ImpersonationBanner />
            <Router>
              <Routes>
              {/* ======== PUBLIC ROUTES ======== */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* ======== DASHBOARD ======== */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />

              {/* ======== CREATIVE STUDIO (Phase B) ======== */}
              <Route
                path="/studio"
                element={
                  <ProtectedRoute>
                    <CreativeStudio />
                  </ProtectedRoute>
                }
              />

              {/* ======== ASSETS ======== */}
              <Route 
                path="/assets" 
                element={
                  <ProtectedRoute>
                    <Assets />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/create" 
                element={
                  <AdminRoute>
                    <AssetCreate />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/assets/:assetId" 
                element={
                  <ProtectedRoute>
                    <AssetDetail />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/:assetId/edit" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <AssetEdit />
                  </ProtectedRoute>
                } 
              />

              {/* ======== SPACES (Office/Warehousing) ======== */}
              <Route 
                path="/spaces/:buildingId" 
                element={
                  <ProtectedRoute>
                    <SpacesManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/spaces/:buildingId/import" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <SpacesImport />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/:assetId/spaces" 
                element={
                  <ProtectedRoute>
                    <SpacesManagement />
                  </ProtectedRoute>
                } 
              />

              {/* ======== UNIT TYPES (Residential) ======== */}
              <Route 
                path="/assets/:assetId/unit-types" 
                element={
                  <ProtectedRoute>
                    <UnitTypesManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/:assetId/units/import" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <UnitsImport />
                  </ProtectedRoute>
                } 
              />

              {/* ======== UNITS PER BUILDING (Residential) ======== */}
              <Route 
                path="/units/:buildingId" 
                element={
                  <ProtectedRoute>
                    <UnitsManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/units/:buildingId/import" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <UnitsImportByBuilding />
                  </ProtectedRoute>
                } 
              />

              {/* ======== CLIENTS ======== */}
              <Route 
                path="/clients" 
                element={
                  <ProtectedRoute>
                    <Clients />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/clients/create" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <ClientCreate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/clients/import" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <ClientImport />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/clients/:clientId" 
                element={
                  <ProtectedRoute>
                    <ClientDetail />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/clients/:clientId/edit" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <ClientEdit />
                  </ProtectedRoute>
                } 
              />

              {/* ======== PLR ======== */}
              <Route 
                path="/plrs" 
                element={
                  <ProtectedRoute>
                    <PLRList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/plrs/create" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales', 'team_member']}>
                    <PLRCreate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/plrs/:plrId/edit" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales', 'team_member']}>
                    <PLREdit />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/plrs/:plrId/analytics" 
                element={
                  <ProtectedRoute>
                    <PLRAnalyticsEnhanced />
                  </ProtectedRoute>
                } 
              />
<Route
  path="/analytics"
  element={
    <ProtectedRoute>
      <AnalyticsDashboard />
    </ProtectedRoute>
  }
/>
<Route
  path="/reports"
  element={
    <ProtectedRoute>
      <Reports />
    </ProtectedRoute>
  }
/>
<Route 
  path="/plrs/:plrId/analytics-enhanced" 
  element={
    <ProtectedRoute>
      <PLRAnalyticsEnhanced />
    </ProtectedRoute>
  } 
/>
              <Route 
                path="/plrs/:plrId/template-builder" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales', 'team_member']}>
                    <TemplateBuilder />
                  </ProtectedRoute>
                } 
              />

              {/* ======== CAMPAIGNS (email + WhatsApp) ======== */}
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute>
                    <EmailCampaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/create"
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <EmailCampaignCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/whatsapp/new"
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <WhatsAppCampaignCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/whatsapp/:campaignId"
                element={
                  <ProtectedRoute>
                    <WhatsAppCampaignDetail />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/campaigns/:campaignId" 
                element={
                  <ProtectedRoute>
                    <EmailCampaignDetail />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/campaigns/:campaignId/edit" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <EmailCampaignCreate />
                  </ProtectedRoute>
                } 
              />

              {/* ======== NOTIFICATIONS ======== */}
              <Route 
                path="/notifications" 
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/notification-preferences" 
                element={
                  <ProtectedRoute>
                    <NotificationPreferences />
                  </ProtectedRoute>
                } 
              />

              {/* ======== REGIONS ======== */}
              <Route 
                path="/regions" 
                element={
                  <ProtectedRoute>
                    <Regions />
                  </ProtectedRoute>
                } 
              />

              {/* ======== USERS (Admin only) ======== */}
              <Route
                path="/users"
                element={
                  <AdminRoute>
                    <Users />
                  </AdminRoute>
                }
              />

              {/* ======== INTEGRATIONS (Admin only) ======== */}
              <Route
                path="/settings/integrations"
                element={
                  <AdminRoute>
                    <Integrations />
                  </AdminRoute>
                }
              />

              {/* ======== CONNECTOR DIRECTORY (P3d preview — SUPER ADMIN only) ====
                  Held-swap: gated to super_admin so the Meta App Review reviewer
                  (an admin-level tenant account) can neither see the menu link nor
                  reach it by URL — the reviewer's Connect-Facebook page stays the
                  only Meta touchpoint they can find. Opens to all admins and
                  replaces /settings/integrations after leads_retrieval clears. */}
              <Route
                path="/settings/connectors"
                element={
                  <SuperAdminRoute>
                    <ConnectorDirectory />
                  </SuperAdminRoute>
                }
              />

              {/* ======== PII ACCESS LOG (Admin only) ======== */}
              <Route
                path="/pii-access-log"
                element={
                  <AdminRoute>
                    <PiiAccessLog />
                  </AdminRoute>
                }
              />

              {/* ======== SUPER ADMIN ======== */}
              <Route 
                path="/admin" 
                element={
                  <SuperAdminRoute>
                    <SuperAdmin />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/crm-integrations"
                element={
                  <SuperAdminRoute>
                    <CRMIntegrations />
                  </SuperAdminRoute>
                }
              />

	{/* ======== AGENT ======== */}
              <Route 
                path="/agent/conversations" 
                element={
                  <ProtectedRoute>
                    <AgentConversations />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/:assetId/agent-kb" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <AgentKB />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assets/:assetId/agent-config" 
                element={
                  <AdminRoute>
                    <AgentConfig />
                  </AdminRoute>
                }
              />
              <Route
                path="/agent/projects"
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'super_admin', 'svp', 'manager', 'sales']}>
                    <SiteAgentProjects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent/automations"
                element={
                  <ProtectedRoute allowedRoles={["client_admin", "super_admin", "svp", "manager"]}>
                    <Automations />
                  </ProtectedRoute>
                }
              />

              {/* ======== REDIRECTS ======== */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>

            {/* React Query Devtools - only in development */}
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
            )}
           </PiiRevealProvider>
          </TenantProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
